import { useEffect, useState } from "react";
import {
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import type {
  DocumentRejectionReason,
  DriverDocumentType,
} from "../../types/domain";

// Wording comes from the change tracker's Notes column, which fixed this list.
const REJECTION_REASONS: { value: DocumentRejectionReason; label: string }[] = [
  { value: "invalidDocument", label: "Invalid document" },
  { value: "wrongSlot", label: "Uploaded on the wrong slot" },
  { value: "expiredDocument", label: "Expired document" },
  { value: "fraudulentDocument", label: "Fraudulent document" },
  { value: "vehicleMismatch", label: "Vehicle mismatch" },
  { value: "other", label: "Other" },
];

export interface ReviewDocumentTarget {
  docType: DriverDocumentType;
  label: string;
}

export function ReviewDocumentModal({
  target,
  onClose,
  onSubmit,
  loading,
}: {
  target: ReviewDocumentTarget | null;
  onClose: () => void;
  onSubmit: (vars: {
    docType: DriverDocumentType;
    rejectionReason: DocumentRejectionReason;
    rejectionComment?: string;
  }) => void;
  loading?: boolean;
}) {
  const [reason, setReason] = useState<DocumentRejectionReason | null>(null);
  const [comment, setComment] = useState("");

  // Reset between documents so a reason picked for the licence does not
  // linger when the admin opens the insurance.
  useEffect(() => {
    if (target) {
      setReason(null);
      setComment("");
    }
  }, [target]);

  // Mirrors the backend rule: "other" alone tells the driver nothing.
  const commentRequired = reason === "other";
  const canSubmit =
    reason != null && (!commentRequired || comment.trim().length > 0);

  return (
    <Modal
      opened={target != null}
      onClose={onClose}
      title={target ? `Reject ${target.label}` : "Reject document"}
      centered
    >
      <Stack>
        <Text size="sm" c="dimmed">
          The driver sees this reason and can re-upload just this document.
        </Text>

        <Select
          label="Reason"
          placeholder="Pick a reason"
          data={REJECTION_REASONS}
          value={reason}
          onChange={(v) => setReason(v as DocumentRejectionReason | null)}
          required
        />

        <Textarea
          label="Comment"
          description={
            commentRequired
              ? "Required when the reason is 'Other'"
              : "Optional extra detail for the driver"
          }
          placeholder="What does the driver need to fix?"
          value={comment}
          onChange={(e) => setComment(e.currentTarget.value)}
          minRows={3}
          autosize
          required={commentRequired}
        />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="red"
            loading={loading}
            disabled={!canSubmit}
            onClick={() =>
              target &&
              reason &&
              onSubmit({
                docType: target.docType,
                rejectionReason: reason,
                rejectionComment: comment.trim() || undefined,
              })
            }
          >
            Reject document
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
